import { fabric } from 'fabric';
import * as THREE from 'three';

export interface CanvasEditorOptions {
  width?: number;
  height?: number;
  backgroundColor?: string;
}

export class CanvasEditor {
  private canvas: fabric.Canvas;
  // Отдельный канвас подложки для UV, чтобы не попадал в экспорт
  private uvBgCanvasEl: HTMLCanvasElement | null = null;
  private uvBgCtx: CanvasRenderingContext2D | null = null;
  private uvCanvasSize: number = 4096; // Размер UV развертки (внутренний размер, не зависит от размера canvas на экране)
  private onChangeCallback?: (dataUrl: string) => void;
  private onRealtimeCallback?: (dataUrl: string) => void;
  private onZoomCallback?: () => void;
  private debounceTimer: number | null = null;
  private isClearing: boolean = false;
  private isLoading: boolean = false;
  private isMoving: boolean = false;
  private didCommitTransformUpdate: boolean = false;
  private lastUpdateTime: number = 0;
  private selectionChangeCallback?: (type: string | null) => void;
  // Навигация: панорамирование
  private isPanning: boolean = false;
  private lastPanPoint: { x: number; y: number } | null = null;
  // Навигация: зум + панорамирование (в экранных пикселях относительно контейнера)
  private zoomFactor: number = 1; // пользовательский зум поверх базового fit
  private panOffsetX: number = 0;
  private panOffsetY: number = 0;
  private baseScale: number = 1;
  private baseOffsetX: number = 0;
  private baseOffsetY: number = 0;
  // Undo/Redo система
  private undoStack: string[] = [];
  private redoStack: string[] = [];
  private maxHistorySize: number = 20;
  private isUndoRedo: boolean = false;
  
  // Профессиональные функции редактирования (как в Illustrator/Figma)
  private isShiftPressed: boolean = false;
  private isAltPressed: boolean = false;
  private isDuplicating: boolean = false;
  private objectStartPosition: { left: number; top: number } | null = null;
  private clipboard: any[] = [];

  // UV background cache (per model + per uvGroup)
  private uvCacheModelKey: string | null = null;
  private uvGroupCanvasCache: Map<string, HTMLCanvasElement> = new Map();

  // ============ Mask (clipPath) tool ============
  private isMaskCreating: boolean = false;
  private maskTarget: fabric.Object | null = null;
  private maskPoints: fabric.Point[] = [];
  private maskPointer: fabric.Point | null = null;
  private maskNearStart: boolean = false;
  private maskSavedTargetState: {
    opacity: number;
    selectable: boolean;
    evented: boolean;
    hasControls: boolean;
    hasBorders: boolean;
  } | null = null;
  private maskSavedCanvasState: {
    selection: boolean;
    skipTargetFind: boolean;
    defaultCursor: string;
    hoverCursor: string;
  } | null = null;
  private maskStateChangeCallback?: (active: boolean) => void;
  private maskAfterRenderHandler?: () => void;
  private maskMouseDownHandler?: (opt: any) => void;
  private maskMouseMoveHandler?: (opt: any) => void;

  constructor(canvasId: string, options: CanvasEditorOptions = {}) {
    const canvasElement = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvasElement) {
      throw new Error(`Canvas element with id "${canvasId}" not found`);
    }

    // Оборачиваем родителя и создаём отдельный канвас ниже основного
    const parent = canvasElement.parentElement as HTMLElement;
    if (parent) {
      if (getComputedStyle(parent).position === 'static') {
        parent.style.position = 'relative';
      }
      // Создаём фон-канвас под Fabric
      this.uvBgCanvasEl = document.createElement('canvas');
      this.uvBgCanvasEl.width = options.width || 512;
      this.uvBgCanvasEl.height = options.height || 512;
      this.uvBgCanvasEl.style.position = 'absolute';
      this.uvBgCanvasEl.style.left = '0';
      this.uvBgCanvasEl.style.top = '0';
      this.uvBgCanvasEl.style.zIndex = '0';
      this.uvBgCanvasEl.style.pointerEvents = 'none';
      parent.insertBefore(this.uvBgCanvasEl, canvasElement);
      this.uvBgCtx = this.uvBgCanvasEl.getContext('2d');
      // Поднимаем основной канвас выше
      (canvasElement as any).style.position = 'relative';
      (canvasElement as any).style.zIndex = '1';
    }

    this.canvas = new fabric.Canvas(canvasId, {
      width: options.width || 512,
      height: options.height || 512,
      backgroundColor: options.backgroundColor || 'transparent', // Прозрачный фон по умолчанию!
      enableRetinaScaling: false // Keep false for performance (realtime 3D updates)
    });
    
    // Отключаем обрезку элементов при выходе за границы canvas
    this.canvas.clipPath = undefined;
    this.canvas.preserveObjectStacking = true;
    this.canvas.controlsAboveOverlay = true;

    this.setupControls();
    this.setupNavigation();
    this.setupPasteHandler();
  }

  public getZoomFactor(): number {
    return this.zoomFactor;
  }

  public resetZoom(): void {
    this.zoomFactor = 1;
    this.panOffsetX = 0;
    this.panOffsetY = 0;
    this.applyViewportTransform();
    
    // Вызываем callback для обновления UI (например, текста кнопки reset zoom)
    if (this.onZoomCallback) {
      this.onZoomCallback();
    }
  }

  public zoomIn(step: number = 1.08): void {
    this.zoomAtCenter(step);
  }

  public zoomOut(step: number = 1.08): void {
    this.zoomAtCenter(1 / step);
  }

  private clampZoom(z: number): number {
    return Math.max(0.25, Math.min(8, z));
  }

  private getContainerRect(): DOMRect | null {
    const container = document.getElementById('uv-areas-container');
    if (!container) return null;
    const r = container.getBoundingClientRect();
    if (!r.width || !r.height) return null;
    return r;
  }

  private applyViewportTransform(): void {
    if (!this.uvBgCanvasEl) return;
    const totalScale = this.baseScale * this.zoomFactor;
    // Округляем offset для предотвращения микросдвигов при дробных пикселях
    const offsetX = Math.round(this.baseOffsetX + this.panOffsetX);
    const offsetY = Math.round(this.baseOffsetY + this.panOffsetY);

    // UV background (CSS)
    this.uvBgCanvasEl.style.transform = `translate(${offsetX}px, ${offsetY}px) scale(${totalScale})`;
    this.uvBgCanvasEl.style.transformOrigin = '0 0';

    // Fabric canvas viewport
    const vpt = this.canvas.viewportTransform;
    if (vpt) {
      vpt[0] = totalScale; // scaleX
      vpt[3] = totalScale; // scaleY
      vpt[4] = offsetX; // translateX
      vpt[5] = offsetY; // translateY
      this.canvas.setViewportTransform(vpt);
    }
  }

  private zoomAtPoint(screenX: number, screenY: number, factor: number): void {
    const totalScale = this.baseScale * this.zoomFactor;
    const offsetX = this.baseOffsetX + this.panOffsetX;
    const offsetY = this.baseOffsetY + this.panOffsetY;

    // screen -> world (UV space 0..4096)
    const worldX = (screenX - offsetX) / totalScale;
    const worldY = (screenY - offsetY) / totalScale;

    const nextZoom = this.clampZoom(this.zoomFactor * factor);
    if (nextZoom === this.zoomFactor) return;
    this.zoomFactor = nextZoom;

    const nextTotalScale = this.baseScale * this.zoomFactor;
    const nextOffsetX = screenX - worldX * nextTotalScale;
    const nextOffsetY = screenY - worldY * nextTotalScale;

    // Округляем panOffset для предотвращения накопления погрешностей
    this.panOffsetX = Math.round(nextOffsetX - this.baseOffsetX);
    this.panOffsetY = Math.round(nextOffsetY - this.baseOffsetY);

    this.applyViewportTransform();
    this.canvas.requestRenderAll();
    
    // Вызываем callback для обновления UI (например, текста кнопки reset zoom)
    if (this.onZoomCallback) {
      this.onZoomCallback();
    }
  }

  private zoomAtCenter(factor: number): void {
    const rect = this.getContainerRect();
    if (!rect) return;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    this.zoomAtPoint(cx, cy, factor);
  }

  /**
   * Обновляет "рабочий" размер канваса под текущий viewport контейнера.
   * Важно: это НЕ влияет на экспорт — экспорт всегда принудительно 4096x4096.
   */
  public setViewportPixelSize(width: number, height: number): void {
    const w = Math.max(1, Math.round(width));
    const h = Math.max(1, Math.round(height));
    this.canvas.setDimensions({ width: w, height: h }, { backstoreOnly: false, cssOnly: false });
    // После изменения размеров нужно пересчитать offset для корректного hit-test/drag
    (this.canvas as any).calcOffset?.();
    this.canvas.requestRenderAll();
  }

  public setOnChangeCallback(callback?: (dataUrl: string) => void): void {
    this.onChangeCallback = callback;
  }

  public setOnRealtimeCallback(callback?: (dataUrl: string) => void): void {
    this.onRealtimeCallback = callback;
  }

  public setOnZoomCallback(callback?: () => void): void {
    this.onZoomCallback = callback;
  }

  public setMaskStateChangeCallback(cb?: (active: boolean) => void): void {
    this.maskStateChangeCallback = cb;
  }

  public isMaskCreationActive(): boolean {
    return this.isMaskCreating;
  }

  public canStartMaskCreation(): boolean {
    const active = this.canvas.getActiveObject();
    if (!active) return false;
    if ((active as any).type === 'activeSelection') return false;
    return true;
  }

  public selectedHasMask(): boolean {
    const active = this.canvas.getActiveObject() as any;
    if (!active) return false;
    if (active.type === 'activeSelection' && Array.isArray(active._objects)) {
      return active._objects.some((o: any) => Boolean(o?.clipPath));
    }
    return Boolean(active.clipPath);
  }

  public startMaskCreation(): boolean {
    if (this.isMaskCreating) return true;
    const active = this.canvas.getActiveObject() as any;
    if (!active) return false;
    if (active.type === 'activeSelection') return false;

    this.isMaskCreating = true;
    this.maskTarget = active as fabric.Object;
    this.maskPoints = [];
    this.maskPointer = null;
    this.maskNearStart = false;

    // Save & adjust canvas interaction so clicks add points, not select/transform
    this.maskSavedCanvasState = {
      selection: Boolean(this.canvas.selection),
      skipTargetFind: Boolean((this.canvas as any).skipTargetFind),
      defaultCursor: this.canvas.defaultCursor || 'default',
      hoverCursor: (this.canvas as any).hoverCursor || 'move'
    };
    this.canvas.selection = false;
    this.canvas.skipTargetFind = true;
    this.canvas.defaultCursor = 'crosshair';
    (this.canvas as any).hoverCursor = 'crosshair';
    this.canvas.discardActiveObject();

    // Make the target semi-transparent and non-interactive while drawing
    const t = this.maskTarget as any;
    this.maskSavedTargetState = {
      opacity: typeof t.opacity === 'number' ? t.opacity : 1,
      selectable: Boolean(t.selectable),
      evented: Boolean(t.evented),
      hasControls: Boolean(t.hasControls),
      hasBorders: Boolean(t.hasBorders)
    };
    t.opacity = 0.5;
    t.selectable = false;
    t.evented = false;
    t.hasControls = false;
    t.hasBorders = false;
    // Disable caching for crisp mask edges
    t.objectCaching = false;
    t.setCoords?.();

    this.maskAfterRenderHandler = () => this.renderMaskOverlay();
    this.maskMouseDownHandler = (opt: any) => this.onMaskMouseDown(opt);
    this.maskMouseMoveHandler = (opt: any) => this.onMaskMouseMove(opt);
    this.canvas.on('after:render', this.maskAfterRenderHandler);
    this.canvas.on('mouse:down', this.maskMouseDownHandler);
    this.canvas.on('mouse:move', this.maskMouseMoveHandler);

    this.canvas.requestRenderAll();
    if (this.maskStateChangeCallback) this.maskStateChangeCallback(true);
    return true;
  }

  public cancelMaskCreation(): void {
    if (!this.isMaskCreating) return;
    const t = this.maskTarget;
    this.cleanupMaskCreation(false);
    if (t) {
      this.canvas.setActiveObject(t);
      this.canvas.requestRenderAll();
    }
  }

  public removeMaskFromSelected(): void {
    const active = this.canvas.getActiveObject() as any;
    if (!active) return;

    if (active.type === 'activeSelection' && Array.isArray(active._objects)) {
      active._objects.forEach((o: any) => {
        if (o) delete o.clipPath;
      });
    } else {
      delete active.clipPath;
    }

    this.canvas.requestRenderAll();
    this.triggerUpdate();
    this.saveHistoryState();
  }

  private setupControls(): void {
    // Настройка контролов для реалтайм обновления
    this.canvas.on('object:modified', (e) => {
      // КРИТИЧЕСКИ ВАЖНО для activeSelection (мультивыбор): пересчитать координаты после трансформаций,
      // иначе Fabric может некорректно отрисовывать/хиттестить объекты.
      const target = (e as any)?.target as any;
      if (target) {
        if (target.type === 'activeSelection' && Array.isArray(target._objects)) {
          // Пересчитываем координаты каждого объекта в группе
          target._objects.forEach((obj: any) => {
            if (obj) {
              obj.setCoords();
              obj.dirty = true;
            }
          });
        }
        target.setCoords();
        target.dirty = true;
      }
      
      // Также пересчитываем координаты ВСЕХ объектов на canvas
      // Это предотвращает проблемы с устаревшими координатами
      this.canvas.getObjects().forEach(obj => {
        obj.setCoords();
        (obj as any).dirty = true;
      });
      
      // Гарантируем, что финальный апдейт пойдёт по "не-движущейся" ветке
      // (обновление 3D делаем после завершения трансформации).
      this.isMoving = false;
      this.didCommitTransformUpdate = true;
      this.triggerUpdate();
      this.saveHistoryState();
    });
    this.canvas.on('object:added', () => {
      this.triggerUpdate();
      this.saveHistoryState();
    });
    this.canvas.on('object:removed', () => {
      this.triggerUpdate();
      this.saveHistoryState();
    });
    
    // Во время движения НЕ обновляем 3D — только отмечаем состояние.
    // Это даёт максимальный FPS при перетаскивании.
    this.canvas.on('object:moving', (e: any) => { 
      this.isMoving = true;
      this.didCommitTransformUpdate = false;
      this.handleObjectMoving(e);
    });
    this.canvas.on('object:scaling', () => {
      this.isMoving = true;
      this.didCommitTransformUpdate = false;
    });
    this.canvas.on('object:rotating', (e: any) => { 
      this.isMoving = true;
      this.didCommitTransformUpdate = false;
      this.handleObjectRotating(e);
    });

    // В Fabric нет object:moved/object:scaled/object:rotated.
    // Сбрасываем флаг движения на mouse:up и пересчитываем coords для activeSelection.
    // Alt+drag дублирование объектов
    // ВАЖНО: clone() - асинхронный, поэтому сохраняем ВСЕ позиции СИНХРОННО
    // ДО начала клонирования, чтобы они не "уплыли" пока идёт async clone()
    this.canvas.on('mouse:down', (e: any) => {
      if (this.isMaskCreating) return;
      this.didCommitTransformUpdate = false;
      if (this.isAltPressed && e.target && !this.isDuplicating) {
        const target = e.target;
        
        // Для ActiveSelection (мультивыбора) клонируем все объекты внутри
        if (target.type === 'activeSelection') {
          this.isDuplicating = true;
          const selection = target as fabric.ActiveSelection;
          const objects = selection.getObjects();
          
          // КРИТИЧЕСКИ ВАЖНО: Сохраняем ВСЕ АБСОЛЮТНЫЕ трансформации СИНХРОННО ДО начала клонирования!
          // Используем qrDecompose для получения АБСОЛЮТНЫХ значений из матрицы трансформации
          const savedTransforms: Array<{
            x: number;
            y: number;
            angle: number;
            scaleX: number;
            scaleY: number;
            flipX: boolean;
            flipY: boolean;
            skewX: number;
            skewY: number;
          }> = [];
          
          objects.forEach((obj: any) => {
            // Получаем АБСОЛЮТНУЮ матрицу трансформации (включая трансформации группы)
            const transformMatrix = obj.calcTransformMatrix();
            
            // Декомпозируем матрицу чтобы получить абсолютные scale, angle, skew
            const decomposed = fabric.util.qrDecompose(transformMatrix);
            
            savedTransforms.push({
              x: decomposed.translateX,
              y: decomposed.translateY,
              angle: decomposed.angle,
              scaleX: decomposed.scaleX,
              scaleY: decomposed.scaleY,
              flipX: obj.flipX || false,
              flipY: obj.flipY || false,
              skewX: decomposed.skewX || 0,
              skewY: decomposed.skewY || 0
            });
          });
          
          // Теперь асинхронно клонируем, используя СОХРАНЁННЫЕ АБСОЛЮТНЫЕ трансформации
          let cloneCount = 0;
          
          objects.forEach((obj: any, index: number) => {
            const saved = savedTransforms[index];
            
            obj.clone((cloned: any) => {
              // Используем СОХРАНЁННЫЕ АБСОЛЮТНЫЕ значения
              cloned.set({
                originX: 'center',
                originY: 'center',
                left: saved.x,
                top: saved.y,
                angle: saved.angle,
                scaleX: saved.scaleX,
                scaleY: saved.scaleY,
                flipX: saved.flipX,
                flipY: saved.flipY,
                skewX: saved.skewX,
                skewY: saved.skewY,
                evented: true
              });
              
              if (cloned.type === 'image') {
                cloned.set({ objectCaching: true });
              }
              
              cloned.setCoords();
              
              // Добавляем клон на canvas (он останется на месте)
              this.canvas.add(cloned);
              cloneCount++;
              
              // Когда все клоны созданы - просто перерисовываем
              // НЕ трогаем оригиналы - они остаются в группе и будут двигаться
              if (cloneCount === objects.length) {
                this.canvas.requestRenderAll();
              }
            }, ['src', 'crossOrigin', 'filters']);
          });
        } else {
          // Одиночный объект
          this.isDuplicating = true;
          const originalTarget = target;
          
          // Сохраняем позицию оригинала ДО начала движения
          const origLeft = originalTarget.left;
          const origTop = originalTarget.top;
          const origAngle = originalTarget.angle || 0;
          const origScaleX = originalTarget.scaleX || 1;
          const origScaleY = originalTarget.scaleY || 1;
          const origFlipX = originalTarget.flipX || false;
          const origFlipY = originalTarget.flipY || false;
          
          // Клонируем объект
          originalTarget.clone((cloned: any) => {
            // Клон остается на месте оригинала (используем сохранённые координаты)
            cloned.set({
              left: origLeft,
              top: origTop,
              angle: origAngle,
              scaleX: origScaleX,
              scaleY: origScaleY,
              flipX: origFlipX,
              flipY: origFlipY,
              evented: true
            });
            
            if (cloned.type === 'image') {
              cloned.set({ objectCaching: true });
            }
            
            cloned.setCoords();
            
            // Добавляем клон НА МЕСТО оригинала (под ним в z-order)
            const origIndex = this.canvas.getObjects().indexOf(originalTarget);
            this.canvas.insertAt(cloned, origIndex, false);
            
            // Перемещаем оригинал на верхний слой
            this.canvas.bringToFront(originalTarget);
            
            // Оригинал остается активным и будет перемещаться
            this.canvas.setActiveObject(originalTarget);
            this.canvas.requestRenderAll();
          }, ['src', 'crossOrigin', 'filters']);
        }
      }
    });
    
    this.canvas.on('mouse:up', () => {
      const wasMoving = this.isMoving;
      this.isMoving = false;
      this.isDuplicating = false; // Сбрасываем флаг дублирования
      this.objectStartPosition = null; // Сбрасываем начальную позицию для Shift
      
      const active = this.canvas.getActiveObject() as any;
      if (active) {
        if (active.type === 'activeSelection' && Array.isArray(active._objects)) {
          // КРИТИЧЕСКИ ВАЖНО: пересчитываем координаты ВСЕХ объектов в выделении
          // и затем самого выделения
          active._objects.forEach((obj: any) => {
            if (obj) {
              obj.setCoords();
              // Помечаем объект как "грязный" для корректной перерисовки
              obj.dirty = true;
            }
          });
        }
        active.setCoords();
        active.dirty = true;
      }
      
      // Также пересчитываем координаты ВСЕХ объектов на canvas
      // Это предотвращает проблемы с "фантомными" координатами
      this.canvas.getObjects().forEach(obj => obj.setCoords());
      
      this.canvas.requestRenderAll();
      // Важно: после отпускания мыши обновляем текстуру принта на 3D модели
      // (в движении не обновляем, чтобы не лагало).
      if (wasMoving && !this.didCommitTransformUpdate) {
        this.triggerUpdate();
      }
      this.didCommitTransformUpdate = false;
    });
    
    this.canvas.on('path:created', () => this.triggerUpdate());

    // Отслеживаем выделение, чтобы UI мог реагировать на тип
    this.canvas.on('selection:created', (e: any) => {
      // Пересчитываем координаты при создании выделения
      const selection = e?.selected;
      if (selection && Array.isArray(selection)) {
        selection.forEach((obj: any) => obj?.setCoords?.());
      }
      const active = this.canvas.getActiveObject();
      if (active) active.setCoords();
      this.notifySelectionChange();
    });
    this.canvas.on('selection:updated', (e: any) => {
      // Пересчитываем координаты при обновлении выделения
      const selection = e?.selected;
      if (selection && Array.isArray(selection)) {
        selection.forEach((obj: any) => obj?.setCoords?.());
      }
      const active = this.canvas.getActiveObject();
      if (active) active.setCoords();
      this.notifySelectionChange();
    });
    this.canvas.on('selection:cleared', () => {
      // При снятии выделения пересчитываем координаты всех объектов
      this.canvas.getObjects().forEach(obj => obj.setCoords());
      this.notifySelectionChange();
    });
  }

  // Обработка движения с Shift (ограничение по осям)
  private handleObjectMoving(e: any): void {
    if (!this.isShiftPressed || !e.target) return;
    
    const obj = e.target;
    
    // Сохраняем начальную позицию при первом движении
    if (!this.objectStartPosition) {
      this.objectStartPosition = { left: obj.left, top: obj.top };
      return;
    }
    
    const deltaX = Math.abs(obj.left - this.objectStartPosition.left);
    const deltaY = Math.abs(obj.top - this.objectStartPosition.top);
    
    // Если движение больше по горизонтали - блокируем вертикаль, иначе наоборот
    if (deltaX > deltaY) {
      obj.set('top', this.objectStartPosition.top);
    } else {
      obj.set('left', this.objectStartPosition.left);
    }
  }
  
  // Обработка вращения с Shift (шаг 15 градусов)
  private handleObjectRotating(e: any): void {
    if (!this.isShiftPressed || !e.target) return;
    
    const obj = e.target;
    const angle = obj.angle || 0;
    const snapAngle = 15; // Шаг привязки в градусах
    
    // Округляем угол к ближайшему кратному snapAngle
    const snappedAngle = Math.round(angle / snapAngle) * snapAngle;
    obj.set('angle', snappedAngle);
  }
  
  // Выделение всех объектов на холсте
  private selectAll(): void {
    const allObjects = this.canvas.getObjects();
    if (allObjects.length === 0) return;
    
    // Сначала discardActiveObject, чтобы не было конфликтов с предыдущим выделением
    this.canvas.discardActiveObject();
    
    // Пересчитываем координаты всех объектов перед созданием выделения
    allObjects.forEach(obj => obj.setCoords());
    
    if (allObjects.length === 1) {
      this.canvas.setActiveObject(allObjects[0]);
    } else {
      const selection = new fabric.ActiveSelection(allObjects, {
        canvas: this.canvas
      });
      selection.setCoords();
      this.canvas.setActiveObject(selection);
    }
    
    this.canvas.requestRenderAll();
    console.log('Selected', allObjects.length, 'objects');
  }
  
  // Копирование выделенных объектов
  private async copySelection(): Promise<void> {
    const activeObjects = this.canvas.getActiveObjects();
    if (activeObjects.length === 0) return;
    
    // Проверяем, является ли выделение группой (ActiveSelection)
    const activeObject = this.canvas.getActiveObject();
    const isGroup = activeObject && activeObject.type === 'activeSelection';
    
    // Если это группа, принудительно обновляем координаты всех объектов
    if (isGroup && activeObject) {
      activeObject.setCoords();
      activeObjects.forEach(obj => obj.setCoords());
    }
    
    // Сохраняем копии объектов в буфер с точными координатами
    const objectsData = activeObjects.map(obj => {
      const objData = obj.toObject(['src', 'crossOrigin', 'filters']);
      
      // КРИТИЧЕСКИ ВАЖНО: Когда объекты в ActiveSelection, их left/top
      // относительны к центру группы. getCenterPoint() может не учитывать
      // это правильно. Используем calcTransformMatrix() для получения
      // АБСОЛЮТНЫХ координат центра объекта.
      const transformMatrix = obj.calcTransformMatrix();
      // Матрица 2D трансформации: [scaleX, skewY, skewX, scaleY, translateX, translateY]
      // translateX (индекс 4) и translateY (индекс 5) дают абсолютные координаты центра
      const absoluteCenterX = transformMatrix[4];
      const absoluteCenterY = transformMatrix[5];
      
      // ВАЖНО: Сохраняем АБСОЛЮТНЫЕ координаты центра объекта
      objData.left = absoluteCenterX;
      objData.top = absoluteCenterY;
      
      // Сохраняем все трансформации явно
      // Для объектов в группе угол может быть суммой углов (объекта + группы)
      // Но scale обычно независимый. Берём из матрицы для точности.
      objData.angle = obj.angle || 0;
      objData.scaleX = obj.scaleX || 1;
      objData.scaleY = obj.scaleY || 1;
      objData.flipX = obj.flipX || false;
      objData.flipY = obj.flipY || false;
      objData.width = obj.width;
      objData.height = obj.height;
      
      // Если объект в группе, нужно учесть угол группы
      if (isGroup && activeObject) {
        const groupAngle = (activeObject as any).angle || 0;
        objData.angle = ((obj.angle || 0) + groupAngle) % 360;
      }
      
      // Используем 'center' как origin для точного позиционирования по центру
      objData.originX = 'center';
      objData.originY = 'center';
      
      console.log('Copy: Object', obj.type, 'at absolute center', absoluteCenterX.toFixed(2), absoluteCenterY.toFixed(2), 
                  'angle:', objData.angle, 'scale:', objData.scaleX, objData.scaleY);
      
      return objData;
    });
    
    this.clipboard = objectsData;
    
    // Также сохраняем в системный буфер обмена для работы между вкладками
    try {
      const clipboardData = {
        type: 'fabric-canvas-objects',
        version: '1.0',
        objects: objectsData,
        // ВАЖНО: координаты объектов живут в UV-пространстве (0..uvCanvasSize),
        // а размеры canvas.getWidth()/getHeight() — это viewport-пиксели и могут
        // отличаться между вкладками. Для корректной вставки между вкладками
        // сохраняем именно UV размер.
        canvasInfo: {
          uvSize: this.uvCanvasSize
        }
      };
      const jsonString = JSON.stringify(clipboardData);
      
      // Используем Clipboard API для сохранения в системный буфер
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(jsonString);
        console.log('Copied', objectsData.length, 'objects to system clipboard with positions');
      } else {
        // Fallback для старых браузеров
        console.log('Copied', objectsData.length, 'objects to internal clipboard (system clipboard not available)');
      }
    } catch (err) {
      // Если не удалось записать в системный буфер, используем только внутренний
      console.warn('Failed to write to system clipboard, using internal clipboard only:', err);
    }
  }
  
  // Вставка из буфера
  private pasteSelection(objectsData?: any[], clipboardInfo?: any): void {
    // Используем переданные данные или внутренний буфер
    // ВАЖНО: делаем КОПИЮ данных, а не ссылку на буфер
    const dataToPaste: any[] = objectsData 
      ? JSON.parse(JSON.stringify(objectsData)) 
      : (this.clipboard.length > 0 ? JSON.parse(JSON.stringify(this.clipboard)) : []);
    
    if (dataToPaste.length === 0) return;
    
    // ВАЖНО: сначала discardActiveObject, чтобы полностью разорвать связь объектов с предыдущим выделением
    this.canvas.discardActiveObject();
    this.canvas.requestRenderAll();
    
    // НЕ очищаем внутренний буфер - пользователь может хотеть вставить несколько раз
    // this.clipboard остаётся для повторных вставок
    
    // Восстанавливаем объекты из буфера с сохранением точных координат
    if (fabric.util && fabric.util.enlivenObjects) {
      fabric.util.enlivenObjects(dataToPaste, (clonedObjects: any[]) => {
        // Если есть информация о UV размере исходного проекта — можем масштабировать координаты.
        // НЕ используем canvas.getWidth()/getHeight(): это viewport-пиксели и они не равны UV координатам.
        const sourceUvSize: number | undefined =
          clipboardInfo?.canvasInfo?.uvSize ??
          clipboardInfo?.canvasInfo?.uvCanvasSize ??
          undefined;
        const currentUvSize = this.uvCanvasSize;
        const scale = sourceUvSize ? currentUvSize / sourceUvSize : 1;
        const useScaling = !!sourceUvSize && Math.abs(scale - 1) > 0.0001;
        
        clonedObjects.forEach((obj, index) => {
          const originalData = dataToPaste[index];
          
          // Восстанавливаем точные координаты из сохраненных данных
          let finalLeft = originalData.left !== undefined && originalData.left !== null ? originalData.left : (obj.left || 0);
          let finalTop = originalData.top !== undefined && originalData.top !== null ? originalData.top : (obj.top || 0);
          
          console.log('Paste: Restoring object', obj.type, 'to center', finalLeft.toFixed(2), finalTop.toFixed(2));
          
          // Если UV размеры разные — масштабируем координаты
          if (useScaling) {
            finalLeft = finalLeft * scale;
            finalTop = finalTop * scale;
            console.log('Paste: Scaled to', finalLeft.toFixed(2), finalTop.toFixed(2), 'uvScale:', scale.toFixed(4));
          }
          
          // КРИТИЧЕСКИ ВАЖНО: Полностью сбрасываем любую связь с группой/selection
          // Fabric.js может сохранять внутренние ссылки на группу
          (obj as any).group = undefined;
          (obj as any).canvas = undefined;
          
          // Восстанавливаем все свойства
          // КРИТИЧЕСКИ ВАЖНО: originX/originY ДОЛЖНЫ быть установлены ДО left/top!
          obj.set({
            originX: originalData.originX || 'center',
            originY: originalData.originY || 'center',
            left: finalLeft,
            top: finalTop,
            angle: originalData.angle !== undefined ? originalData.angle : (obj.angle || 0),
            scaleX: originalData.scaleX !== undefined ? originalData.scaleX : (obj.scaleX || 1),
            scaleY: originalData.scaleY !== undefined ? originalData.scaleY : (obj.scaleY || 1),
            flipX: originalData.flipX !== undefined ? originalData.flipX : (obj.flipX || false),
            flipY: originalData.flipY !== undefined ? originalData.flipY : (obj.flipY || false),
            // Сбрасываем любые флаги активности
            active: false
          });
          
          // Принудительно обновляем координаты ДО добавления на canvas
          obj.setCoords();
          
          // Добавляем объект на canvas
          this.canvas.add(obj);
          
          // Финальное обновление координат после добавления
          obj.setCoords();
          
          console.log('Paste: Object placed at', obj.left?.toFixed(2), obj.top?.toFixed(2), 'with origin', obj.originX, obj.originY);
        });
        
        // Выделяем вставленные объекты
        if (clonedObjects.length === 1) {
          this.canvas.setActiveObject(clonedObjects[0]);
        } else if (clonedObjects.length > 1) {
          // Создаём НОВУЮ ActiveSelection из свежих объектов
          const selection = new fabric.ActiveSelection(clonedObjects, {
            canvas: this.canvas
          });
          // Принудительно пересчитываем координаты всех объектов в selection
          selection.setCoords();
          clonedObjects.forEach(obj => obj.setCoords());
          this.canvas.setActiveObject(selection);
        }
        
        this.canvas.requestRenderAll();
        this.triggerUpdate();
        console.log('Pasted', clonedObjects.length, 'objects at original positions');
      }, 'fabric');
    }
  }

  private setupPasteHandler(): void {
    // Обработчик события copy для установки данных в системный буфер
    document.addEventListener('copy', (e: ClipboardEvent) => {
      const modal = document.getElementById('canvas-editor-modal');
      if (!modal || modal.style.display === 'none') return;
      
      // Если есть объекты в буфере, устанавливаем их в clipboardData
      if (this.clipboard.length > 0 && e.clipboardData) {
        try {
          const clipboardData = {
            type: 'fabric-canvas-objects',
            version: '1.0',
            objects: this.clipboard,
            // Сохраняем UV размер (а не viewport размер canvas)
            canvasInfo: {
              uvSize: this.uvCanvasSize
            }
          };
          e.clipboardData.setData('text/plain', JSON.stringify(clipboardData));
          // Также устанавливаем как text/html для совместимости
          e.clipboardData.setData('text/html', JSON.stringify(clipboardData));
        } catch (err) {
          console.warn('Failed to set clipboard data:', err);
        }
      }
    });

    // Обработчик клавиш: Delete, Undo, Redo, Copy, Paste
    document.addEventListener('keydown', (e: KeyboardEvent) => {
      // Проверяем, что редактор активен (модальное окно видно)
      const modal = document.getElementById('canvas-editor-modal');
      if (!modal || modal.style.display === 'none') return;

      // Отслеживаем состояние Shift и Alt
      this.isShiftPressed = e.shiftKey;
      this.isAltPressed = e.altKey;

      // Не обрабатываем если фокус в текстовом поле
      const activeEl = document.activeElement;
      if (activeEl && (activeEl.tagName === 'INPUT' || activeEl.tagName === 'TEXTAREA')) return;

      // Mask creation mode shortcuts have priority
      if (this.isMaskCreating) {
        if (e.key === 'Escape') {
          e.preventDefault();
          this.cancelMaskCreation();
          return;
        }
        if (e.key === 'Enter') {
          e.preventDefault();
          this.finishMaskCreation();
          return;
        }
        if (e.key === 'Backspace' || e.key === 'Delete') {
          e.preventDefault();
          this.removeLastMaskPoint();
          return;
        }
      }

      // Cmd+Z (Mac) или Ctrl+Z (Windows) — Undo
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && !e.shiftKey) {
        e.preventDefault();
        this.undo();
        return;
      }
      
      // Cmd+Shift+Z (Mac) или Ctrl+Shift+Z / Ctrl+Y (Windows) — Redo
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'z' && e.shiftKey) {
        e.preventDefault();
        this.redo();
        return;
      }
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'y') {
        e.preventDefault();
        this.redo();
        return;
      }
      
      // Cmd+A / Ctrl+A — Select All
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'a') {
        e.preventDefault();
        this.selectAll();
        return;
      }
      
      // Cmd+C / Ctrl+C — Copy
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'c') {
        e.preventDefault();
        this.copySelection().catch(err => {
          console.warn('Failed to copy to clipboard:', err);
        });
        return;
      }
      
      // Cmd+V / Ctrl+V — НЕ перехватываем здесь, обрабатывается в событии paste
      // чтобы работал и внутренний буфер и системный (изображения из других приложений)

      // Delete или Backspace для удаления
      if (e.key === 'Delete' || e.key === 'Backspace') {
        const activeObject = this.canvas.getActiveObject();
        if (!activeObject) return;
        
        e.preventDefault();
        
        // Получаем объекты ДО discardActiveObject, иначе getActiveObjects() вернёт пустой массив
        const objectsToRemove = this.canvas.getActiveObjects();
        
        // ВАЖНО: сначала discardActiveObject, чтобы полностью разорвать ActiveSelection
        // Это разрывает связь между объектами и группой выделения
        this.canvas.discardActiveObject();
        
        // Теперь удаляем объекты
        objectsToRemove.forEach(obj => {
          // Сбрасываем любые внутренние ссылки на группу
          (obj as any).group = undefined;
          this.canvas.remove(obj);
        });
        
        // Принудительно обновляем координаты всех оставшихся объектов
        this.canvas.getObjects().forEach(obj => obj.setCoords());
        
        this.canvas.requestRenderAll();
        this.triggerUpdate();
      }
    });
    
    // Отслеживаем отпускание клавиш Shift и Alt
    document.addEventListener('keyup', (e: KeyboardEvent) => {
      if (e.key === 'Shift') {
        this.isShiftPressed = false;
        this.objectStartPosition = null; // Сбрасываем начальную позицию
      }
      if (e.key === 'Alt') {
        this.isAltPressed = false;
      }
    });

    // Обработчик вставки из буфера
    document.addEventListener('paste', (e: ClipboardEvent) => {
      // Проверяем, что редактор активен (модальное окно видно)
      const modal = document.getElementById('canvas-editor-modal');
      if (!modal || modal.style.display === 'none') return;

      const clipboardData = e.clipboardData;
      if (!clipboardData) return;

      const textData = clipboardData.getData('text/plain');
      const htmlData = clipboardData.getData('text/html');
      const items = clipboardData.items;
      
      // ПРИОРИТЕТ 1: SVG (Illustrator часто кладет SVG в text/html + PNG превью как image/png).
      // Если в буфере есть SVG — вставляем как SVG, даже если есть image/* превью.
      const svgFromClipboard = this.extractSVGFromClipboard(textData, htmlData);
      if (svgFromClipboard) {
        e.preventDefault();
        this.pasteSVG(svgFromClipboard);
        return;
      }

      // ПРИОРИТЕТ 2: Проверяем наличие ИЗОБРАЖЕНИЙ в системном буфере
      // (имеет высокий приоритет - если пользователь копирует картинку, он хочет вставить именно её)
      let hasImage = false;
      for (let i = 0; i < items.length; i++) {
        if (items[i].type.startsWith('image/')) {
          hasImage = true;
          break;
        }
      }
      
      if (hasImage) {
        for (let i = 0; i < items.length; i++) {
          const item = items[i];
          
          // SVG как файл
          if (item.type === 'image/svg+xml') {
            e.preventDefault();
            const file = item.getAsFile();
            if (file) {
              const reader = new FileReader();
              reader.onload = (event) => {
                const svgContent = event.target?.result as string;
                if (svgContent) {
                  this.pasteSVG(svgContent);
                }
              };
              reader.readAsText(file);
            }
            return;
          }

          // Обычное изображение (PNG, JPEG и т.д.)
          if (item.type.startsWith('image/')) {
            e.preventDefault();
            const file = item.getAsFile();
            if (file) {
              const reader = new FileReader();
              reader.onload = (event) => {
                const dataUrl = event.target?.result as string;
                if (dataUrl) {
                  this.addImageFromDataURL(dataUrl);
                }
              };
              reader.readAsDataURL(file);
            }
            return;
          }
        }
      }
      
      // ПРИОРИТЕТ 3: Проверяем наличие объектов Fabric.js в системном буфере (из другой вкладки)
      if (textData) {
        try {
          const parsed = JSON.parse(textData);
          if (parsed && parsed.type === 'fabric-canvas-objects' && parsed.objects && Array.isArray(parsed.objects)) {
            e.preventDefault();
            this.pasteSelection(parsed.objects, parsed);
            return;
          }
        } catch (err) {
          // Не JSON или не наш формат - продолжаем
        }
      }

      // ПРИОРИТЕТ 4: Внутренний буфер (объекты, скопированные Cmd+C внутри редактора)
      if (this.clipboard.length > 0) {
        e.preventDefault();
        this.pasteSelection();
        return;
      }

      // ПРИОРИТЕТ 5: SVG в текстовом формате (fallback)
      if (textData && this.isSVGContent(textData)) {
        e.preventDefault();
        this.pasteSVG(textData);
        return;
      }
    });
  }

  private isSVGContent(text: string): boolean {
    const trimmed = text.trim();
    const lower = trimmed.toLowerCase();
    return lower.includes('<svg') || (lower.includes('<?xml') && lower.includes('<svg'));
  }

  private extractSVGFromClipboard(textPlain?: string, textHtml?: string): string | null {
    const candidates = [textPlain, textHtml].filter(Boolean) as string[];
    for (const raw of candidates) {
      const lower = raw.toLowerCase();
      const start = lower.indexOf('<svg');
      if (start === -1) continue;
      const end = lower.lastIndexOf('</svg>');
      if (end === -1) continue;
      const svg = raw.slice(start, end + '</svg>'.length).trim();
      if (this.isSVGContent(svg)) return svg;
    }
    return null;
  }

  private pasteSVG(svgContent: string): void {
    fabric.loadSVGFromString(svgContent, (objects, options) => {
      if (!objects || objects.length === 0) {
        console.warn('No objects found in SVG');
        return;
      }

      // Создаём группу из SVG объектов без принудительного рескейла,
      // чтобы сохранить исходное качество и размеры.
      const svgGroup = fabric.util.groupSVGElements(objects, options);
      
      // ВАЖНО: originX/originY должны быть установлены ДО left/top
      svgGroup.set({
        originX: 'center',
        originY: 'center',
        left: this.uvCanvasSize / 2,
        top: this.uvCanvasSize / 2,
        scaleX: 1,
        scaleY: 1
      });

      this.canvas.add(svgGroup);
      this.canvas.setActiveObject(svgGroup);
      this.canvas.renderAll();
      this.triggerUpdate();
    });
  }

  private addImageFromDataURL(dataUrl: string): void {
    fabric.Image.fromURL(dataUrl, (img) => {
      if (!img) return;

      // Не масштабируем — сохраняем исходное качество и размер изображения.
      // ВАЖНО: originX/originY должны быть установлены ДО left/top
      img.set({
        originX: 'center',
        originY: 'center',
        left: this.uvCanvasSize / 2,
        top: this.uvCanvasSize / 2,
        scaleX: 1,
        scaleY: 1,
        // Кеширование ускоряет перемещение/трансформации больших изображений
        objectCaching: true
      });
      img.setCoords();

      this.canvas.add(img);
      this.canvas.setActiveObject(img);
      this.canvas.renderAll();
      this.triggerUpdate();
    }, { crossOrigin: dataUrl.startsWith('data:') ? undefined as any : 'anonymous' });
  }

  private notifySelectionChange(): void {
    const active = this.canvas.getActiveObject();
    const type = active ? (active as any).type : null;
    if (this.selectionChangeCallback) this.selectionChangeCallback(type || null);
  }

  public setSelectionChangeCallback(cb?: (type: string | null) => void): void {
    this.selectionChangeCallback = cb;
  }

  private triggerUpdate(): void {
    this.triggerUpdateWithQuality(1.0);
  }

  private triggerUpdateWithQuality(quality: number): void {
    // Пропускаем обновления во время очистки canvas
    if (this.isClearing || this.isLoading) return;
    
    // Для движущихся объектов: троттлинг (2ms) для максимальной плавности
    const now = Date.now();
    if (this.isMoving) {
      // Если прошло меньше 2ms с последнего обновления - пропускаем
      if (now - this.lastUpdateTime < .2) {
        return;
      }
      // Обновляем через requestAnimationFrame для синхронизации с отрисовкой браузера
      if (this.debounceTimer) {
        cancelAnimationFrame(this.debounceTimer);
      }
      this.debounceTimer = requestAnimationFrame(() => {
        this.lastUpdateTime = Date.now();
        if (this.onRealtimeCallback && !this.isClearing) {
          // Экспортируем в меньшем разрешении, но масштабируем обратно до 4096x4096
          const lowResSize = Math.max(512, Math.round(this.uvCanvasSize * quality)); // Минимум 256px для приемлемого качества
          const dataUrl = this.exportToDataURLWithResolution('png', 1.0, lowResSize);
          this.onRealtimeCallback(dataUrl);
        }
        this.debounceTimer = null;
      });
      return;
    }
    
    // Для недвижущихся обновлений делаем без задержки
    if (!this.isMoving) {
      if (this.debounceTimer) {
        cancelAnimationFrame(this.debounceTimer);
      }
      this.debounceTimer = requestAnimationFrame(() => {
        if (this.onChangeCallback && !this.isClearing) {
          const dataUrl = this.exportToDataURL('png', quality);
          this.onChangeCallback(dataUrl);
          this.lastUpdateTime = Date.now();
        }
      });
      return;
    }
    
    // Throttle через requestAnimationFrame для оптимизации производительности
    if (this.debounceTimer) {
      cancelAnimationFrame(this.debounceTimer);
    }
    
    this.debounceTimer = requestAnimationFrame(() => {
      if (this.onChangeCallback && !this.isClearing) {
        const dataUrl = this.exportToDataURL('png', quality);
        this.onChangeCallback(dataUrl);
        this.lastUpdateTime = Date.now();
      }
    });
  }

  public addText(text: string = 'Текст'): void {
    const textObj = new fabric.Text(text, {
      left: 100,
      top: 100,
      fontSize: 40,
      fill: '#000000',
      fontFamily: 'Arial'
    });
    this.canvas.add(textObj);
    this.canvas.setActiveObject(textObj);
    this.canvas.renderAll();
  }

  public addRect(): void {
    const rect = new fabric.Rect({
      left: 100,
      top: 100,
      width: 100,
      height: 100,
      fill: '#ff0000',
      stroke: '#000000',
      strokeWidth: 2
    });
    this.canvas.add(rect);
    this.canvas.setActiveObject(rect);
    this.canvas.renderAll();
  }

  public addCircle(): void {
    const circle = new fabric.Circle({
      left: 100,
      top: 100,
      radius: 50,
      fill: '#00ff00',
      stroke: '#000000',
      strokeWidth: 2
    });
    this.canvas.add(circle);
    this.canvas.setActiveObject(circle);
    this.canvas.renderAll();
  }

  public addImage(url: string, scaleToFit: boolean = false): Promise<void> {
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(url, (img) => {
        if (!img) {
          reject(new Error('Failed to load image'));
          return;
        }
        
        // Получаем размеры канваса
        const canvasWidth = this.canvas.getWidth();
        const canvasHeight = this.canvas.getHeight();
        
        if (scaleToFit) {
          // Опциональный режим подгонки (используем только если явно передан)
          img.scaleToWidth(200);
          // ВАЖНО: originX/originY должны быть установлены ДО left/top
          img.set({
            originX: 'center',
            originY: 'center',
            left: canvasWidth / 2,
            top: canvasHeight / 2
          });
        } else {
          // Исходный размер и положение по центру холста
          // ВАЖНО: originX/originY должны быть установлены ДО left/top
          img.set({
            originX: 'center',
            originY: 'center',
            left: this.uvCanvasSize / 2,
            top: this.uvCanvasSize / 2,
            scaleX: 1,
            scaleY: 1,
            angle: 0,
            skewX: 0,
            skewY: 0,
            flipX: false,
            flipY: false
          });
        }
        
        // Для плавности перемещения/масштабирования лучше включить кеш
        img.set({ objectCaching: true });
        img.setCoords();
        // Округляем координаты, чтобы избежать полупиксельных смещений
        img.set({ left: Math.round(img.left || 0), top: Math.round(img.top || 0) });
        this.canvas.add(img);
        this.canvas.setActiveObject(img);
        this.canvas.renderAll();
        resolve();
      }, { crossOrigin: url.startsWith('data:') ? undefined as any : 'anonymous' });
    });
  }

  /**
   * Загружает уже "запечённую" текстуру принта (как на модели)
   * и растягивает её на весь 4096x4096 холст, чтобы первое редактирование
   * старых проектов не давало визуальный "скачок" между 2D и 3D.
   */
  public loadBakedPrintTexture(url: string): Promise<void> {
    return new Promise((resolve, reject) => {
      fabric.Image.fromURL(url, (img) => {
        if (!img) {
          reject(new Error('Failed to load baked print texture'));
          return;
        }

        // Очищаем только объекты, НЕ трогаем viewport и настройки canvas
        const objects = this.canvas.getObjects();
        objects.forEach(obj => this.canvas.remove(obj));

        // Масштабируем текстуру на весь 4096x4096 холст
        const imgWidth = img.width || 1;
        const imgHeight = img.height || 1;
        const scaleX = this.uvCanvasSize / imgWidth;
        const scaleY = this.uvCanvasSize / imgHeight;
        const scale = Math.min(scaleX, scaleY);

        // ВАЖНО: originX/originY должны быть установлены ДО left/top
        img.set({
          originX: 'left',
          originY: 'top',
          left: 0,
          top: 0,
          scaleX: scale,
          scaleY: scale,
          angle: 0,
          skewX: 0,
          skewY: 0,
          flipX: false,
          flipY: false,
          objectCaching: true
        });
        img.setCoords();

        this.canvas.add(img);
        this.canvas.setActiveObject(img);
        this.canvas.renderAll();
        resolve();
      }, { crossOrigin: url.startsWith('data:') ? undefined as any : 'anonymous' });
    });
  }

  public deleteSelected(): void {
    const activeObject = this.canvas.getActiveObject();
    if (!activeObject) return;
    
    // Получаем объекты ДО discardActiveObject
    const objectsToRemove = this.canvas.getActiveObjects();
    
    // ВАЖНО: сначала discardActiveObject, чтобы полностью разорвать ActiveSelection
    this.canvas.discardActiveObject();
    
    // Теперь удаляем объекты
    objectsToRemove.forEach(obj => {
      // Сбрасываем любые внутренние ссылки на группу
      (obj as any).group = undefined;
      this.canvas.remove(obj);
    });
    
    // Принудительно обновляем координаты всех оставшихся объектов
    this.canvas.getObjects().forEach(obj => obj.setCoords());
    
    this.canvas.requestRenderAll();
  }

  public clear(): void {
    this.isClearing = true;
    this.canvas.clear();
    this.canvas.backgroundColor = 'transparent';
    this.canvas.renderAll();
    // Даём время на завершение обработки событий
    setTimeout(() => {
      this.isClearing = false;
      this.clearHistory(); // Очищаем историю при полной очистке canvas
    }, 100);
  }

  public setBackgroundColor(color: string): void {
    this.canvas.backgroundColor = color;
    this.canvas.renderAll();
  }

  public exportToDataURL(format: string = 'png', quality: number = 1.0): string {
    // Сохраняем текущий viewportTransform
    const savedVpt = this.canvas.viewportTransform;
    const savedZoom = this.canvas.getZoom();
    
    // Временно сбрасываем viewportTransform для экспорта (зум и панорамирование не должны влиять на экспорт)
    this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    this.canvas.setZoom(1);
    
    // ВАЖНО: Всегда экспортируем в 4096x4096, независимо от размера canvas на экране
    const exportSize = this.uvCanvasSize; // 4096x4096
    const currentWidth = this.canvas.getWidth();
    const currentHeight = this.canvas.getHeight();
    
    // Временно устанавливаем размер canvas в 4096x4096 для экспорта
    if (currentWidth !== exportSize || currentHeight !== exportSize) {
      // Сбрасываем кеш всех объектов перед изменением размера
      // чтобы они перерисовались в полном разрешении (4096x4096)
      this.canvas.getObjects().forEach(obj => {
        (obj as any).dirty = true;
        // Сбрасываем внутренний кеш если он есть
        if ((obj as any)._cacheCanvas) {
          (obj as any)._cacheCanvas = null;
        }
      });
      
      this.canvas.setDimensions({ width: exportSize, height: exportSize }, { backstoreOnly: false, cssOnly: false });
      this.canvas.renderAll();
    }
    
    // Перерисовываем canvas без границ выделения в полном разрешении
    this.canvas.renderAll();
    
    // Используем toCanvasElement для экспорта только объектов без границ выделения
    // Если метод доступен, используем его, иначе используем обычный getElement
    const canvasElement = (this.canvas as any).toCanvasElement ? 
      (this.canvas as any).toCanvasElement() : 
      this.canvas.getElement();
    
    // Создаем временный canvas размером 4096x4096
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = exportSize;
    tempCanvas.height = exportSize;
    const tempCtx = tempCanvas.getContext('2d')!;
    
    // Применяем только поворот на 180° и отзеркаливание по горизонтали,
    // как и при выводе UV в setUVBackground
    tempCtx.translate(exportSize / 2, exportSize / 2);
    tempCtx.rotate(Math.PI);
    tempCtx.scale(-1, 1);
    tempCtx.translate(-exportSize / 2, -exportSize / 2);
    
    // Рисуем содержимое canvas (без границ выделения) в полном разрешении
    tempCtx.drawImage(canvasElement, 0, 0);
    
    // Восстанавливаем размер canvas обратно
    if (currentWidth !== exportSize || currentHeight !== exportSize) {
      this.canvas.setDimensions({ width: currentWidth, height: currentHeight }, { backstoreOnly: false, cssOnly: false });
    }
    
    // Восстанавливаем viewportTransform
    if (savedVpt) {
      this.canvas.setViewportTransform(savedVpt);
      this.canvas.setZoom(savedZoom);
      this.canvas.renderAll();
    }
    
    // Экспортируем трансформированный canvas
    return tempCanvas.toDataURL(`image/${format}`, quality);
  }

  private exportToDataURLWithResolution(format: string = 'png', quality: number = 1.0, targetSize: number): string {
    // Сохраняем текущий viewportTransform
    const savedVpt = this.canvas.viewportTransform;
    const savedZoom = this.canvas.getZoom();
    
    // Временно сбрасываем viewportTransform для экспорта
    this.canvas.setViewportTransform([1, 0, 0, 1, 0, 0]);
    this.canvas.setZoom(1);
    
    // ВАЖНО: Всегда экспортируем в 4096x4096, независимо от размера canvas на экране
    const exportSize = this.uvCanvasSize; // 4096x4096
    const currentWidth = this.canvas.getWidth();
    const currentHeight = this.canvas.getHeight();
    
    // Временно устанавливаем размер canvas в 4096x4096 для экспорта
    if (currentWidth !== exportSize || currentHeight !== exportSize) {
      // Сбрасываем кеш всех объектов перед изменением размера
      // чтобы они перерисовались в полном разрешении (4096x4096)
      this.canvas.getObjects().forEach(obj => {
        (obj as any).dirty = true;
        // Сбрасываем внутренний кеш если он есть
        if ((obj as any)._cacheCanvas) {
          (obj as any)._cacheCanvas = null;
        }
      });
      
      this.canvas.setDimensions({ width: exportSize, height: exportSize }, { backstoreOnly: false, cssOnly: false });
      this.canvas.renderAll();
    }
    
    // Перерисовываем canvas без границ выделения в полном разрешении
    this.canvas.renderAll();
    
    // Используем toCanvasElement для экспорта только объектов без границ выделения
    const canvasElement = (this.canvas as any).toCanvasElement ? 
      (this.canvas as any).toCanvasElement() : 
      this.canvas.getElement();
    
    // Создаем временный canvas размером targetSize (низкое разрешение)
    const tempCanvas = document.createElement('canvas');
    tempCanvas.width = targetSize;
    tempCanvas.height = targetSize;
    const tempCtx = tempCanvas.getContext('2d')!;
    
    // Рисуем содержимое canvas в низком разрешении (масштабируем при рисовании)
    tempCtx.drawImage(canvasElement, 0, 0, exportSize, exportSize, 0, 0, targetSize, targetSize);
    
    // Теперь масштабируем обратно до 4096x4096 для сохранения физического размера
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = exportSize;
    finalCanvas.height = exportSize;
    const finalCtx = finalCanvas.getContext('2d')!;
    
    // Применяем трансформации: поворот на 180° и отзеркаливание по горизонтали
    finalCtx.translate(exportSize / 2, exportSize / 2);
    finalCtx.rotate(Math.PI);
    finalCtx.scale(-1, 1);
    finalCtx.translate(-exportSize / 2, -exportSize / 2);
    
    // Масштабируем низкоразрешенное изображение обратно до 4096x4096
    finalCtx.drawImage(tempCanvas, 0, 0, targetSize, targetSize, 0, 0, exportSize, exportSize);
    
    // Восстанавливаем размер canvas обратно
    if (currentWidth !== exportSize || currentHeight !== exportSize) {
      this.canvas.setDimensions({ width: currentWidth, height: currentHeight }, { backstoreOnly: false, cssOnly: false });
    }
    
    // Восстанавливаем viewportTransform
    if (savedVpt) {
      this.canvas.setViewportTransform(savedVpt);
      this.canvas.setZoom(savedZoom);
      this.canvas.renderAll();
    }
    
    // Экспортируем финальный canvas в полном размере 4096x4096
    return finalCanvas.toDataURL(`image/${format}`, quality);
  }

  public saveCanvasData(): any {
    // Сохраняем все объекты canvas с их трансформациями
    const objects = this.canvas.getObjects();
    console.log('saveCanvasData: saving', objects.length, 'objects');
    
    const savedObjects = objects.map(obj => {
      // КРИТИЧЕСКИ ВАЖНО: ВСЕГДА сохраняем координаты ЦЕНТРА объекта
      // используя calcTransformMatrix(). Это единственный надёжный способ
      // получить абсолютные координаты независимо от originX/originY.
      const transformMatrix = obj.calcTransformMatrix();
      const centerX = transformMatrix[4];
      const centerY = transformMatrix[5];
      
      // Для изображений сохраняем все свойства включая src
      if (obj.type === 'image') {
        const objJson = obj.toObject([
          'crossOrigin', 
          'filters',
          'width',
          'height'
        ]);
        
        // Сохраняем координаты ЦЕНТРА с center origin
        objJson.originX = 'center';
        objJson.originY = 'center';
        objJson.left = centerX;
        objJson.top = centerY;
        objJson.scaleX = obj.scaleX || 1;
        objJson.scaleY = obj.scaleY || 1;
        objJson.angle = obj.angle || 0;
        objJson.flipX = obj.flipX || false;
        objJson.flipY = obj.flipY || false;
        objJson.width = obj.width;
        objJson.height = obj.height;
        
        let stableSrc = '';
        try {
          if ((obj as any).getSrc) stableSrc = (obj as any).getSrc();
          if (!stableSrc && (obj as any).src) stableSrc = (obj as any).src;
          if (!stableSrc && (obj as any).element?.src) stableSrc = (obj as any).element.src;
        } catch {}
        if (stableSrc) objJson.src = stableSrc;
        
        console.log('saveCanvasData: image at center', centerX.toFixed(2), centerY.toFixed(2));
        return objJson;
      } else {
        // Для остальных объектов
        const objJson = obj.toObject();
        
        // Сохраняем координаты ЦЕНТРА с center origin
        objJson.originX = 'center';
        objJson.originY = 'center';
        objJson.left = centerX;
        objJson.top = centerY;
        objJson.scaleX = obj.scaleX || 1;
        objJson.scaleY = obj.scaleY || 1;
        objJson.angle = obj.angle || 0;
        objJson.flipX = obj.flipX || false;
        objJson.flipY = obj.flipY || false;
        
        console.log('saveCanvasData:', objJson.type, 'at center', centerX.toFixed(2), centerY.toFixed(2));
        return objJson;
      }
    });
    
    return { objects: savedObjects };
  }


  public loadCanvasData(data: any): Promise<void> {
    return new Promise((resolve) => {
      // Блокируем обновления на время загрузки
      this.isLoading = true;
      this.isClearing = true;
      
      // НЕ трогаем viewport — он уже установлен через setUVBackground()
      // ВСЕГДА очищаем объекты (не фон), даже если новые данные пустые
      const existingObjects = this.canvas.getObjects();
      existingObjects.forEach(obj => this.canvas.remove(obj));
      
      if (!data || !data.objects || data.objects.length === 0) {
        console.log('loadCanvasData: no data to load, canvas cleared');
        this.isClearing = false;
        this.isLoading = false;
        this.canvas.renderAll();
        resolve();
        return;
      }
      
      console.log('loadCanvasData: loading', data.objects.length, 'objects');
      
      // Используем fabric.util.enlivenObjects для восстановления всех объектов
      if (fabric.util && fabric.util.enlivenObjects) {
        fabric.util.enlivenObjects(data.objects, (loadedObjects: any[]) => {
          console.log('loadCanvasData: enlivenObjects returned', loadedObjects.length, 'objects');
          loadedObjects.forEach((obj, index) => {
            const originalData = data.objects[index];
            
            // Получаем сохранённые данные
            const savedOriginX = originalData.originX || 'left';
            const savedOriginY = originalData.originY || 'top';
            const savedLeft = originalData.left !== undefined ? originalData.left : obj.left;
            const savedTop = originalData.top !== undefined ? originalData.top : obj.top;
            const savedScaleX = originalData.scaleX !== undefined ? originalData.scaleX : (obj.scaleX || 1);
            const savedScaleY = originalData.scaleY !== undefined ? originalData.scaleY : (obj.scaleY || 1);
            const savedAngle = originalData.angle !== undefined ? originalData.angle : (obj.angle || 0);
            
            console.log('loadCanvasData: loading', obj.type, 'savedOrigin:', savedOriginX, savedOriginY, 
                        'savedPos:', savedLeft?.toFixed?.(2), savedTop?.toFixed?.(2));
            
            // Если данные сохранены с center origin (новый формат) - используем напрямую
            // Если с другим origin (старый формат) - конвертируем в center
            let centerX = savedLeft;
            let centerY = savedTop;
            
            // Конвертация для обратной совместимости со старыми данными
            if (savedOriginX !== 'center' || savedOriginY !== 'center') {
              const objWidth = obj.width || originalData.width || 100;
              const objHeight = obj.height || originalData.height || 100;
              const halfWidth = (objWidth * savedScaleX) / 2;
              const halfHeight = (objHeight * savedScaleY) / 2;
              
              // Конвертируем в центр (без учёта угла - это приблизительно, но лучше чем ничего)
              if (savedOriginX === 'left') centerX = savedLeft + halfWidth;
              else if (savedOriginX === 'right') centerX = savedLeft - halfWidth;
              
              if (savedOriginY === 'top') centerY = savedTop + halfHeight;
              else if (savedOriginY === 'bottom') centerY = savedTop - halfHeight;
              
              console.log('loadCanvasData: converted to center:', centerX.toFixed(2), centerY.toFixed(2));
            }
            
            // Устанавливаем ВСЕ свойства с center origin
            obj.set({
              originX: 'center',
              originY: 'center',
              left: centerX,
              top: centerY,
              scaleX: savedScaleX,
              scaleY: savedScaleY,
              angle: savedAngle,
              flipX: originalData.flipX || false,
              flipY: originalData.flipY || false
            });
            
            if (obj.type === 'image') {
              obj.set({ objectCaching: true });
            }
            
            obj.setCoords();
            this.canvas.add(obj);
            
            console.log('loadCanvasData: placed at', obj.left?.toFixed(2), obj.top?.toFixed(2));
          });
          this.canvas.renderAll();
          this.isClearing = false;
          this.isLoading = false;
          this.clearHistory();
          this.triggerUpdate();
          resolve();
        }, 'fabric');
      } else {
        // Fallback если enlivenObjects не доступен
        console.warn('fabric.util.enlivenObjects not available');
        this.isClearing = false;
        this.isLoading = false;
        resolve();
      }
    });
  }

  public exportToBlob(): Promise<Blob | null> {
    return new Promise((resolve) => {
      this.canvas.getElement().toBlob((blob) => {
        resolve(blob);
      }, 'image/png', 1.0);
    });
  }

  /**
   * Рендерит canvasData в dataURL в том же формате, что и редактор (4096×4096, поворот 180° и flip).
   * Используется при загрузке проекта, чтобы принты отображались в тех же позициях, что и в редакторе.
   */
  public static renderCanvasDataToDataURL(canvasData: any): Promise<string | null> {
    if (!canvasData?.objects?.length) return Promise.resolve(null);
    const id = 'temp-print-export-' + Date.now() + '-' + Math.random().toString(36).slice(2);
    const wrapper = document.createElement('div');
    wrapper.style.cssText = 'position:absolute;left:-9999px;top:0;width:4096px;height:4096px;overflow:hidden;';
    const canvasEl = document.createElement('canvas');
    canvasEl.id = id;
    canvasEl.width = 4096;
    canvasEl.height = 4096;
    wrapper.appendChild(canvasEl);
    document.body.appendChild(wrapper);
    return (async () => {
      try {
        const editor = new CanvasEditor(id, { width: 4096, height: 4096, backgroundColor: 'transparent' });
        await editor.loadCanvasData(canvasData);
        const dataUrl = editor.exportToDataURL('png', 1.0);
        return dataUrl;
      } catch (e) {
        console.warn('renderCanvasDataToDataURL failed:', e);
        return null;
      } finally {
        wrapper.remove();
      }
    })();
  }

  public getCanvas(): fabric.Canvas {
    return this.canvas;
  }

  public resetViewport(): void {
    // Масштабируем развертку по ширине контейнера и центрируем
    this.syncUVBackgroundWithCanvas();
  }

  /** Возвращает список UV-групп (id → имена мешей) найденных в модели */
  public getModelUVGroups(model: THREE.Group): Record<string, string[]> {
    const groups: Record<string, string[]> = {};
    model.traverse((child) => {
      if (child instanceof THREE.Mesh && !child.name.startsWith('Print_')) {
        const gid = (child as any).userData?.uvGroup as string | undefined;
        if (gid) {
          if (!groups[gid]) groups[gid] = [];
          groups[gid].push(child.name || gid);
        }
      }
    });
    return groups;
  }

  public setUVBackground(model: THREE.Group, filterGroupIds?: string[] | null): void {
    // Удаляем предыдущий UV фон если есть
    this.clearUVBackground();

    // Создаем реальную UV развертку модели (4096x4096), с опциональной фильтрацией по группе
    const uvCanvas = this.createModelUVMap(model, filterGroupIds || undefined);

    // Рисуем UV на отдельном фоновом канвасе, чтобы он не попадал в экспорт
    if (this.uvBgCtx && this.uvBgCanvasEl) {
      // ВАЖНО: Используем размер UV canvas (4096x4096), а не размер fabric canvas на экране
      // Это гарантирует, что UV развертка всегда рисуется в полном разрешении
      const uvSize = this.uvCanvasSize;
      
      // Убеждаемся, что UV background canvas имеет правильный размер
      if (this.uvBgCanvasEl.width !== uvSize || this.uvBgCanvasEl.height !== uvSize) {
        this.uvBgCanvasEl.width = uvSize;
        this.uvBgCanvasEl.height = uvSize;
      }
      
      this.uvBgCtx.save();
      this.uvBgCtx.globalAlpha = 0.4;
      // Рисуем UV развертку в полном разрешении (4096x4096) без дополнительных трансформаций
      this.uvBgCtx.drawImage(uvCanvas, 0, 0, uvSize, uvSize);
      this.uvBgCtx.restore();
      
        // Сбрасываем CSS transform при установке нового UV background
        // Он будет обновляться через syncUVBackgroundWithCanvas при навигации
        if (this.uvBgCanvasEl) {
          this.uvBgCanvasEl.style.transform = '';
          this.syncUVBackgroundWithCanvas();
        }
    }
  }

  private createModelUVMap(model: THREE.Group, filterGroupIds?: string[]): HTMLCanvasElement {
    const size = 4096;

    // Reset cache when model changes
    const modelKey = (model as any).uuid || 'model';
    if (this.uvCacheModelKey !== modelKey) {
      this.uvCacheModelKey = modelKey;
      this.uvGroupCanvasCache.clear();
    }

    const normGroups =
      filterGroupIds && filterGroupIds.length > 0
        ? Array.from(new Set(filterGroupIds)).filter(Boolean)
        : null;

    // Быстрый путь: если выбрана ровно 1 группа — используем кэш
    if (normGroups && normGroups.length === 1) {
      const gid = normGroups[0];
      const cached = this.uvGroupCanvasCache.get(gid);
      if (cached) return cached;
      const single = this.drawUVForGroups(model, [gid], size);
      this.uvGroupCanvasCache.set(gid, single);
      return single;
    }

    // Если выбрано несколько групп — композим из кэша/рендера каждой
    if (normGroups && normGroups.length > 1) {
      const canvas = document.createElement('canvas');
      canvas.width = size;
      canvas.height = size;
      const ctx = canvas.getContext('2d')!;
      normGroups.forEach((gid) => {
        let layer = this.uvGroupCanvasCache.get(gid);
        if (!layer) {
          layer = this.drawUVForGroups(model, [gid], size);
          this.uvGroupCanvasCache.set(gid, layer);
        }
        ctx.drawImage(layer, 0, 0);
      });
      // grid overlay once
      this.drawUVGrid(ctx, size);
      return canvas;
    }

    // Нет фильтра — рисуем все меши (без кэша, т.к. это редкий кейс)
    return this.drawUVForGroups(model, null, size);
  }

  private drawUVGrid(ctx: CanvasRenderingContext2D, size: number): void {
    ctx.strokeStyle = '#333333';
    ctx.lineWidth = 1;
    const gridSize = size / 16;
    for (let i = 0; i <= 16; i++) {
      ctx.beginPath();
      ctx.moveTo(i * gridSize, 0);
      ctx.lineTo(i * gridSize, size);
      ctx.stroke();

      ctx.beginPath();
      ctx.moveTo(0, i * gridSize);
      ctx.lineTo(size, i * gridSize);
      ctx.stroke();
    }
  }

  private drawUVForGroups(model: THREE.Group, filterGroupIds: string[] | null, size: number): HTMLCanvasElement {
    const canvas = document.createElement('canvas');
    canvas.width = size;
    canvas.height = size;
    const ctx = canvas.getContext('2d')!;
    // Прозрачный фон — НИЧЕГО не заливаем, чтобы подложка UV была полностью прозрачной
    
    // Собираем меши из модели, опционально фильтруя по UV-группам.
    // Принт-меши (Print_*) всегда пропускаем — они клоны базовых мешей и не должны дублировать UV.
    const meshes: THREE.Mesh[] = [];
    model.traverse((child) => {
      if (child instanceof THREE.Mesh) {
        if (child.name.startsWith('Print_')) return; // Только базовые меши модели
        if (filterGroupIds && filterGroupIds.length > 0) {
          const gid = (child as any).userData?.uvGroup as string | undefined;
          if (!gid || !filterGroupIds.includes(gid)) return;
        }
        meshes.push(child);
      }
    });
    
    // Рисуем UV развертку для каждого меша
    meshes.forEach((mesh) => {
      const geometry = mesh.geometry;
      const uvAttribute = geometry.getAttribute('uv');
      
      if (!uvAttribute) return;
      
      const positions = geometry.getAttribute('position');
      const indices = geometry.getIndex();
      
      // Цвет для каждого меша - закомментировано, используем серый
      // const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff'];
      // const color = colors[meshIndex % colors.length];
      
      // Серый цвет для всех мешей
      ctx.strokeStyle = '#666666';
      ctx.fillStyle = '#66666640'; // Полупрозрачная заливка
      ctx.lineWidth = 2;
      
      // Рисуем треугольники
      const triangleCount = indices ? indices.count / 3 : positions.count / 3;
      
      for (let i = 0; i < triangleCount; i++) {
        const a = indices ? indices.getX(i * 3) : i * 3;
        const b = indices ? indices.getX(i * 3 + 1) : i * 3 + 1;
        const c = indices ? indices.getX(i * 3 + 2) : i * 3 + 2;
        
        // Three.js загружает текстуры с flipY=true: canvas y=0 (верх) → UV v=0 (низ OpenGL).
        // Поэтому UV v=0 должен быть вверху canvas, а v=1 — внизу: y = v * size.
        const uvA = {
          x: uvAttribute.getX(a) * size,
          y: uvAttribute.getY(a) * size
        };
        const uvB = {
          x: uvAttribute.getX(b) * size,
          y: uvAttribute.getY(b) * size
        };
        const uvC = {
          x: uvAttribute.getX(c) * size,
          y: uvAttribute.getY(c) * size
        };
        
        // Рисуем треугольник
        ctx.beginPath();
        ctx.moveTo(uvA.x, uvA.y);
        ctx.lineTo(uvB.x, uvB.y);
        ctx.lineTo(uvC.x, uvC.y);
        ctx.closePath();
        ctx.fill();
        ctx.stroke();
      }
    });

    // Добавляем сетку для ориентации
    this.drawUVGrid(ctx, size);
    return canvas;
  }

  public clearUVBackground(): void {
    // Полностью очищаем отдельный фоновый канвас
    if (this.uvBgCtx && this.uvBgCanvasEl) {
      this.uvBgCtx.clearRect(0, 0, this.uvBgCanvasEl.width, this.uvBgCanvasEl.height);
    }
  }

  public setColor(color: string): void {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      if (activeObject instanceof fabric.Text) {
        activeObject.set('fill', color);
      } else {
        activeObject.set('fill', color);
      }
      this.canvas.renderAll();
    }
  }

  public setBorderColor(color: string): void {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      activeObject.set('stroke', color);
      this.canvas.renderAll();
    }
  }

  public setFontSize(size: number): void {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject instanceof fabric.Text) {
      activeObject.set('fontSize', size);
      this.canvas.renderAll();
    }
  }

  public setOpacity(opacity: number): void {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      activeObject.set('opacity', opacity);
      this.canvas.renderAll();
    }
  }

  public bringToFront(): void {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      this.canvas.bringToFront(activeObject);
      this.canvas.renderAll();
    }
  }

  public sendToBack(): void {
    const activeObject = this.canvas.getActiveObject();
    if (activeObject) {
      this.canvas.sendToBack(activeObject);
      this.canvas.renderAll();
    }
  }

  private setupNavigation(): void {
    const canvasElement = this.canvas.getElement();
    const container = canvasElement.closest('#canvas-wrapper') as HTMLElement || 
                      canvasElement.parentElement || 
                      document.getElementById('uv-areas-container');
    if (!container) {
      console.warn('Navigation container not found');
      return;
    }

    // Отслеживание нажатия пробела для панорамирования
    let isSpacePressed = false;
    
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.code === 'Space' && !e.repeat) {
        // Когда редактор открыт, пробел часто "кликает" по сфокусированным кнопкам/элементам
        // или скроллит страницу, что может приводить к повторному открытию редактора и сбросу зума.
        // Поэтому при открытом редакторе всегда блокируем дефолтное поведение пробела
        // (кроме случая, когда фокус в поле ввода).
        const modal = document.getElementById('canvas-editor-modal');
        const modalVisible = Boolean(modal && modal.style.display !== 'none');
        const activeEl = document.activeElement as HTMLElement | null;
        const isTyping =
          !!activeEl &&
          (activeEl.tagName === 'INPUT' ||
            activeEl.tagName === 'TEXTAREA' ||
            activeEl.isContentEditable);
        if (modalVisible && !isTyping) {
          e.preventDefault();
          e.stopPropagation();
        }

        isSpacePressed = true;
        canvasElement.style.cursor = 'grab';
        // (старое условие оставляем как fallback, если редактор почему-то не определился)
        if (container.contains(e.target as Node) || e.target === canvasElement) e.preventDefault();
      }
    };

    const handleKeyUp = (e: KeyboardEvent) => {
      if (e.code === 'Space') {
        const modal = document.getElementById('canvas-editor-modal');
        const modalVisible = Boolean(modal && modal.style.display !== 'none');
        if (modalVisible) {
          e.preventDefault();
          e.stopPropagation();
        }
        isSpacePressed = false;
        this.isPanning = false;
        this.lastPanPoint = null;
        canvasElement.style.cursor = 'default';
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('keyup', handleKeyUp);

    // Панорамирование при зажатом пробеле
    const handleMouseDown = (e: MouseEvent) => {
      if (isSpacePressed && e.button === 0) {
        this.isPanning = true;
        this.lastPanPoint = { x: e.clientX, y: e.clientY };
        canvasElement.style.cursor = 'grabbing';
        // Временно отключаем selection
        const wasSelectionEnabled = this.canvas.selection;
        this.canvas.selection = false;
        (this as any)._wasSelectionEnabled = wasSelectionEnabled;
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (this.isPanning && this.lastPanPoint && isSpacePressed) {
        const deltaX = e.clientX - this.lastPanPoint.x;
        const deltaY = e.clientY - this.lastPanPoint.y;

        // Панорамирование в экранных пикселях (округляем для предотвращения микросдвигов)
        this.panOffsetX = Math.round(this.panOffsetX + deltaX);
        this.panOffsetY = Math.round(this.panOffsetY + deltaY);
        this.applyViewportTransform();
        this.canvas.renderAll();
        
        this.lastPanPoint = { x: e.clientX, y: e.clientY };
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (this.isPanning) {
        this.isPanning = false;
        this.lastPanPoint = null;
        if ((this as any)._wasSelectionEnabled !== undefined) {
          this.canvas.selection = (this as any)._wasSelectionEnabled;
        }
        if (isSpacePressed) {
          canvasElement.style.cursor = 'grab';
        } else {
          canvasElement.style.cursor = 'default';
        }
        e.preventDefault();
        e.stopPropagation();
      }
    };

    const handleMouseLeave = () => {
      if (this.isPanning) {
        this.isPanning = false;
        this.lastPanPoint = null;
        if ((this as any)._wasSelectionEnabled !== undefined) {
          this.canvas.selection = (this as any)._wasSelectionEnabled;
        }
        if (isSpacePressed) {
          canvasElement.style.cursor = 'grab';
        } else {
          canvasElement.style.cursor = 'default';
        }
      }
    };

    // Используем capture phase для перехвата до Fabric.js
    container.addEventListener('mousedown', handleMouseDown, true);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    container.addEventListener('mouseleave', handleMouseLeave, true);

    // Зум: Ctrl/⌘ + колесо мыши (или трекпад)
    const handleWheel = (e: WheelEvent) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      // Чтобы не скроллился контейнер/страница
      e.preventDefault();

      const rect = container.getBoundingClientRect();
      const x = e.clientX - rect.left;
      const y = e.clientY - rect.top;

      // wheel deltaY > 0 -> zoom out, < 0 -> zoom in
      // Более отзывчивый зум - увеличен шаг с 1.08 до 1.12
      const step = 1.12;
      const factor = e.deltaY < 0 ? step : 1 / step;
      this.zoomAtPoint(x, y, factor);
    };
    container.addEventListener('wheel', handleWheel, { passive: false });

    // Сохраняем обработчики для очистки
    (this as any)._navigationHandlers = {
      keydown: handleKeyDown,
      keyup: handleKeyUp,
      mousedown: handleMouseDown,
      mousemove: handleMouseMove,
      mouseup: handleMouseUp,
      mouseleave: handleMouseLeave,
      wheel: handleWheel,
      container
    };
  }

  public syncUVBackgroundWithCanvas(): void {
    // Масштабируем развертку по ширине контейнера и центрируем
    if (!this.uvBgCanvasEl) return;
    
    const container = document.getElementById('uv-areas-container');
    if (!container) return;
    
    const containerRect = container.getBoundingClientRect();
    if (containerRect.width === 0) return;
    
    // КРИТИЧЕСКИ ВАЖНО: Округляем размеры контейнера до целых пикселей
    // Это предотвращает микросдвиги из-за дробных пикселей при повторных открытиях
    const containerWidth = Math.round(containerRect.width);
    const containerHeight = Math.round(containerRect.height);
    
    // Разверстка всегда 4096x4096
    const uvSize = this.uvCanvasSize;
    
    // Масштаб "вписать" (по меньшей стороне), чтобы развертка никогда не обрезалась
    const fitSize = Math.min(containerWidth, containerHeight);
    const scale = fitSize / uvSize;
    
    // Вычисляем размеры после масштабирования (округляем для стабильности)
    const scaledWidth = Math.round(uvSize * scale);
    const scaledHeight = Math.round(uvSize * scale);
    
    // Центрируем по горизонтали и вертикали (округляем offset для предотвращения микросдвигов)
    const offsetX = Math.round((containerWidth - scaledWidth) / 2);
    const offsetY = Math.round((containerHeight - scaledHeight) / 2);
    
    // Сохраняем базовый fit-трансформ и применяем с учетом zoom/pan
    this.baseScale = scale;
    this.baseOffsetX = offsetX;
    this.baseOffsetY = offsetY;
    this.applyViewportTransform();
  }

  // ============ Undo/Redo система ============
  
  private saveHistoryState(): void {
    if (this.isUndoRedo || this.isClearing || this.isLoading) return;
    
    const state = JSON.stringify(this.canvas.toJSON(['src']));
    
    // Не сохраняем если состояние не изменилось
    if (this.undoStack.length > 0 && this.undoStack[this.undoStack.length - 1] === state) {
      return;
    }
    
    this.undoStack.push(state);
    
    // Ограничиваем размер истории
    if (this.undoStack.length > this.maxHistorySize) {
      this.undoStack.shift();
    }
    
    // Очищаем redo при новом действии
    this.redoStack = [];
  }
  
  private undo(): void {
    if (this.undoStack.length <= 1) return; // Нужно минимум 2 состояния
    
    this.isUndoRedo = true;
    
    // Сохраняем текущее состояние в redo
    const currentState = this.undoStack.pop()!;
    this.redoStack.push(currentState);
    
    // Восстанавливаем предыдущее состояние
    const prevState = this.undoStack[this.undoStack.length - 1];
    this.canvas.loadFromJSON(prevState, () => {
      this.canvas.renderAll();
      this.isUndoRedo = false;
      this.triggerUpdate();
    });
  }
  
  private redo(): void {
    if (this.redoStack.length === 0) return;
    
    this.isUndoRedo = true;
    
    // Восстанавливаем состояние из redo
    const nextState = this.redoStack.pop()!;
    this.undoStack.push(nextState);
    
    this.canvas.loadFromJSON(nextState, () => {
      this.canvas.renderAll();
      this.isUndoRedo = false;
      this.triggerUpdate();
    });
  }
  
  public clearHistory(): void {
    this.undoStack = [];
    this.redoStack = [];
    // Сохраняем текущее состояние как начальное
    this.saveHistoryState();
  }

  public destroy(): void {
    // Удаляем обработчики навигации
    const handlers = (this as any)._navigationHandlers;
    if (handlers) {
      window.removeEventListener('keydown', handlers.keydown);
      window.removeEventListener('keyup', handlers.keyup);
      document.removeEventListener('mousemove', handlers.mousemove);
      document.removeEventListener('mouseup', handlers.mouseup);
      if (handlers.container) {
        handlers.container.removeEventListener('mousedown', handlers.mousedown);
        handlers.container.removeEventListener('mouseleave', handlers.mouseleave);
        if (handlers.wheel) handlers.container.removeEventListener('wheel', handlers.wheel);
      }
    }
    
    // Удаляем фоновый канвас UV
    if (this.uvBgCanvasEl && this.uvBgCanvasEl.parentElement) {
      this.uvBgCanvasEl.parentElement.removeChild(this.uvBgCanvasEl);
    }
    this.canvas.dispose();
  }

  // ============ Mask tool internals ============
  private getWorldCloseThreshold(): number {
    const vpt = this.canvas.viewportTransform;
    const scale = vpt ? vpt[0] : 1;
    const px = 12; // screen px
    return px / (scale || 1);
  }

  private onMaskMouseDown(opt: any): void {
    if (!this.isMaskCreating) return;
    const evt = opt?.e;
    if (!evt) return;
    // only primary button
    if (typeof evt.button === 'number' && evt.button !== 0) return;

    const p = this.canvas.getPointer(evt);
    const snapped = new fabric.Point(Math.round(p.x), Math.round(p.y));
    const threshold = this.getWorldCloseThreshold();

    if (this.maskPoints.length >= 3) {
      const first = this.maskPoints[0];
      const dx = snapped.x - first.x;
      const dy = snapped.y - first.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist <= threshold) {
        this.finishMaskCreation(true);
        return;
      }
    }

    this.maskPoints.push(snapped);
    this.maskPointer = snapped;
    this.canvas.requestRenderAll();
  }

  private onMaskMouseMove(opt: any): void {
    if (!this.isMaskCreating) return;
    const evt = opt?.e;
    if (!evt) return;

    const p = this.canvas.getPointer(evt);
    this.maskPointer = new fabric.Point(Math.round(p.x), Math.round(p.y));

    if (this.maskPoints.length >= 3) {
      const first = this.maskPoints[0];
      const threshold = this.getWorldCloseThreshold();
      const dx = (this.maskPointer.x - first.x);
      const dy = (this.maskPointer.y - first.y);
      const dist = Math.sqrt(dx * dx + dy * dy);
      this.maskNearStart = dist <= threshold;
    } else {
      this.maskNearStart = false;
    }

    this.canvas.requestRenderAll();
  }

  private removeLastMaskPoint(): void {
    if (!this.isMaskCreating) return;
    if (this.maskPoints.length === 0) return;
    this.maskPoints.pop();
    this.maskNearStart = false;
    this.canvas.requestRenderAll();
  }

  private finishMaskCreation(_closedByClick: boolean = false): void {
    if (!this.isMaskCreating) return;
    if (!this.maskTarget) {
      this.cleanupMaskCreation(false);
      return;
    }
    if (this.maskPoints.length < 3) {
      // Not enough points -> just cancel
      const t = this.maskTarget;
      this.cleanupMaskCreation(false);
      if (t) this.canvas.setActiveObject(t);
      return;
    }

    const target = this.maskTarget;

    // Convert world points -> target local points
    const m = target.calcTransformMatrix();
    const inv = fabric.util.invertTransform(m);
    const local = this.maskPoints.map((pt) => {
      const p = fabric.util.transformPoint(pt, inv);
      return { x: p.x, y: p.y };
    });

    // Compute mask center in target local coords to position polygon
    let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
    local.forEach(p => {
      if (p.x < minX) minX = p.x;
      if (p.y < minY) minY = p.y;
      if (p.x > maxX) maxX = p.x;
      if (p.y > maxY) maxY = p.y;
    });
    const cx = (minX + maxX) / 2;
    const cy = (minY + maxY) / 2;
    const polyPoints = local.map(p => ({ x: p.x - cx, y: p.y - cy }));

    const clip = new fabric.Polygon(polyPoints, {
      originX: 'center',
      originY: 'center',
      left: cx,
      top: cy,
      fill: '#000', // Required for clipping
      stroke: undefined, // No stroke for cleaner edges
      strokeWidth: 0,
      objectCaching: true, // Enable caching for performance
      perPixelTargetFind: false // Faster hit detection
    });
    (clip as any).absolutePositioned = false;
    (clip as any).inverted = false;
    clip.selectable = false;
    clip.evented = false;
    clip.objectCaching = true;

    (target as any).clipPath = clip;
    (target as any).dirty = true;
    target.setCoords?.();

    this.cleanupMaskCreation(true);

    // Re-select target after applying mask
    this.canvas.setActiveObject(target);
    this.canvas.requestRenderAll();
    this.triggerUpdate();
    this.saveHistoryState();
  }

  private cleanupMaskCreation(keepMaskApplied: boolean): void {
    // Remove listeners
    if (this.maskAfterRenderHandler) this.canvas.off('after:render', this.maskAfterRenderHandler);
    if (this.maskMouseDownHandler) this.canvas.off('mouse:down', this.maskMouseDownHandler);
    if (this.maskMouseMoveHandler) this.canvas.off('mouse:move', this.maskMouseMoveHandler);
    this.maskAfterRenderHandler = undefined;
    this.maskMouseDownHandler = undefined;
    this.maskMouseMoveHandler = undefined;

    // Clear overlay
    try {
      const ctx = (this.canvas as any).contextTop as CanvasRenderingContext2D | undefined;
      if (ctx) this.canvas.clearContext(ctx);
    } catch {}

    // Restore canvas state
    if (this.maskSavedCanvasState) {
      this.canvas.selection = this.maskSavedCanvasState.selection;
      this.canvas.skipTargetFind = this.maskSavedCanvasState.skipTargetFind;
      this.canvas.defaultCursor = this.maskSavedCanvasState.defaultCursor;
      (this.canvas as any).hoverCursor = this.maskSavedCanvasState.hoverCursor;
    }

    // Restore target state (unless it was removed somehow)
    if (this.maskTarget && this.maskSavedTargetState) {
      const t = this.maskTarget as any;
      t.opacity = keepMaskApplied ? this.maskSavedTargetState.opacity : this.maskSavedTargetState.opacity;
      t.selectable = this.maskSavedTargetState.selectable;
      t.evented = this.maskSavedTargetState.evented;
      t.hasControls = this.maskSavedTargetState.hasControls;
      t.hasBorders = this.maskSavedTargetState.hasBorders;
      // Re-enable caching after mask is applied/cancelled
      if (t.type === 'image') {
        t.objectCaching = true;
      }
      t.setCoords?.();
    }

    this.isMaskCreating = false;
    this.maskTarget = null;
    this.maskPoints = [];
    this.maskPointer = null;
    this.maskNearStart = false;
    this.maskSavedTargetState = null;
    this.maskSavedCanvasState = null;

    this.canvas.requestRenderAll();
    if (this.maskStateChangeCallback) this.maskStateChangeCallback(false);
  }

  private renderMaskOverlay(): void {
    if (!this.isMaskCreating) return;
    const ctx = (this.canvas as any).contextTop as CanvasRenderingContext2D | undefined;
    if (!ctx) return;

    this.canvas.clearContext(ctx);

    const vpt = this.canvas.viewportTransform;
    const scale = vpt ? vpt[0] : 1;
    const r = 4 / (scale || 1);

    ctx.save();
    // Disable image smoothing for crisp overlay lines
    ctx.imageSmoothingEnabled = false;
    if (vpt) {
      ctx.transform(vpt[0], vpt[1], vpt[2], vpt[3], vpt[4], vpt[5]);
    }

    // Line
    if (this.maskPoints.length > 0) {
      ctx.beginPath();
      ctx.moveTo(this.maskPoints[0].x, this.maskPoints[0].y);
      for (let i = 1; i < this.maskPoints.length; i++) {
        ctx.lineTo(this.maskPoints[i].x, this.maskPoints[i].y);
      }
      if (this.maskPointer) {
        ctx.lineTo(this.maskPointer.x, this.maskPointer.y);
      }
      ctx.strokeStyle = 'rgba(0, 229, 255, 0.95)';
      ctx.lineWidth = 2 / (scale || 1);
      ctx.stroke();
    }

    // Points
    for (let i = 0; i < this.maskPoints.length; i++) {
      const p = this.maskPoints[i];
      const isFirst = i === 0;
      ctx.beginPath();
      ctx.arc(p.x, p.y, isFirst ? r * 1.3 : r, 0, Math.PI * 2);
      if (isFirst && this.maskNearStart && this.maskPoints.length >= 3) {
        ctx.fillStyle = 'rgba(0, 255, 160, 0.95)';
      } else {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
      }
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.45)';
      ctx.lineWidth = 1 / (scale || 1);
      ctx.stroke();
    }

    ctx.restore();
  }
}
